import { useState, useEffect } from 'react';
import EditableTile from '@/common/EditableTile';
import EditableDropdown, { IDropdownOption } from '@/common/EditableDropdown';
import Toggle from '@/common/Toggle';
import RadioGroup from '@/common/RadioGroup';
import TextAreaInput from '@/common/TextArea/Input';
import { IPutterStaticDropdownData, IPutterDynamicDropdownData } from './useEditPutter';
import { HandHelper, LoftLieHelper, ShaftLengthHelper, SwingWeightHelper } from '@/library';
import { TourBagDetail } from '@/api/models';
import { maskFloatInput } from '@/utils';
import { IPutterClub } from './Putter';
import clubStyles from '../Club.module.scss';
import { handAbbreviationToFullName } from '@/library/HandHelper';
interface IEditPutterProps {
  title: 'Create Club' | 'Edit Club';
  putter: TourBagDetail;
  handDropdownData: HandHelper.IHandOptionMap | undefined;
  loftDropdownData: LoftLieHelper.ILoftOptionMap | undefined;
  lieDropdownData: LoftLieHelper.ILieOptionMap | undefined;
  putterStaticDropdownData: IPutterStaticDropdownData | undefined;
  putterDynamicDropdownData: IPutterDynamicDropdownData | undefined;
  shaftLengthDropdownData: ShaftLengthHelper.ShaftLengthOptionMap | undefined;
  swingWtDropdownData: SwingWeightHelper.ISwingWtOptionMap | undefined;
  // eslint-disable-next-line no-unused-vars
  handleSave: (putterClub: IPutterClub) => Promise<void>;
  handleCancel: () => void;
}

function EditPutter({
  putter,
  handDropdownData,
  loftDropdownData,
  lieDropdownData,
  putterStaticDropdownData,
  putterDynamicDropdownData,
  shaftLengthDropdownData,
  swingWtDropdownData,
  handleSave,
  handleCancel,
}: IEditPutterProps) {
  // dropdown state
  const [putterHeadModelOptions, setPutterHeadModelOptions] = useState<IDropdownOption[]>([]);
  const [putterHeadFinishOptions, setPutterHeadFinishOptions] = useState<IDropdownOption[]>([]);
  const [putterSightlineOptions, setPutterSightlineOptions] = useState<IDropdownOption[]>([]);
  const [putterFaceOptions, setPutterFaceOptions] = useState<IDropdownOption[]>([]);
  const [putterNeckOptions, setPutterNeckOptions] = useState<IDropdownOption[]>([]);
  const [putterShaftOptions, setPutterShaftOptions] = useState<IDropdownOption[]>([]);
  const [putterGripOptions, setPutterGripOptions] = useState<IDropdownOption[]>([]);

  // field state
  const [isActive, setIsActive] = useState(putter.isActive);
  const [putterHand, setPutterHand] = useState(handAbbreviationToFullName(putter.hand));
  const [putterLevelId, setPutterLevelId] = useState(putter.putterLevelId);
  const [putterTypeId, setPutterTypeId] = useState(putter.putterTypeId);
  const [putterHeadModelId, setPutterHeadModelId] = useState(putter.headModelId || '');
  const [putterHeadFinishId, setPutterHeadFinishId] = useState(putter.headFinishId || '');
  const [putterSightlineId, setPutterSightlineId] = useState(putter.putterSightlineId || '');
  const [putterFaceId, setPutterFaceId] = useState(putter.putterFaceId || '');
  const [putterNeckId, setPutterNeckId] = useState(putter.putterNeckId || '');
  const [putterFinalHW, setPutterFinalHW] = useState(putter.putterFinalHW || '');
  const [loft, setLoft] = useState(`${putter.loftDigit}${putter.loftFraction}`);
  const [lie, setLie] = useState(`${putter.lieDigit}${putter.lieFraction}`);
  const [swingWt, setSwingWt] = useState(`${putter.swingWtPrefixName}${putter.swingWtSuffixName}`);
  const [soleWtId, setSoleWtId] = useState(putter.putterWeightId || '');
  const [stamping, setStamping] = useState(putter.stamping);
  const [painting, setPainting] = useState(putter.paintingNotes);
  const [putterShaftId, setPutterShaftId] = useState(putter.putterShaftId || '');
  const [measured, setMeasured] = useState(putter.isShaftLengthFinished ? true : false);
  const [putterShaftLength, setPutterShaftLength] = useState(`${putter.shaftLengthDigit} ${putter.shaftLengthFraction}`);
  const [putterGripId, setPutterGripId] = useState(putter.putterGripId || '');
  const [putterGripTape, setPutterGripTape] = useState(putter.tape);
  const [notes, setNotes] = useState(putter.notes);
  const itemNo = putter.itemNo;

  const cancelEdition = () => {
    // TODO: We can detect if its creation/ edition mode and reset or not these values
    // reset values
    setIsActive(putter.isActive);
    setPutterHand(handAbbreviationToFullName(putter.hand));
    setPutterLevelId(putter.putterLevelId);
    setPutterTypeId(putter.putterTypeId);
    setPutterHeadModelId(putter.headModelId || '');
    setPutterHeadFinishId(putter.headFinishId || '');
    setPutterSightlineId(putter.putterSightlineId || '');
    setPutterFaceId(putter.putterFaceId || '');
    setPutterNeckId(putter.putterNeckId || '');
    setPutterFinalHW(putter.putterFinalHW || '');
    setLoft(`${putter.loftDigit}${putter.loftFraction}`);
    setLie(`${putter.lieDigit}${putter.lieFraction}`);
    setSwingWt(`${putter.swingWtPrefixName}${putter.swingWtSuffixName}`);
    setSoleWtId(putter.putterWeightId || '');
    setStamping(putter.stamping);
    setPainting(putter.paintingNotes);
    setPutterShaftId(putter.putterShaftId || '');
    setMeasured(putter.isShaftLengthFinished ? true : false);
    setPutterShaftLength(`${putter.shaftLengthDigit} ${putter.shaftLengthFraction}`);
    setPutterGripId(putter.putterGripId || '');
    setPutterGripTape(putter.tape);
    setNotes(putter.notes);
    // exit
    handleCancel();
  };

  const savePutter = async () => {
    const putterClub: IPutterClub = {
      isActive,
      putterHand,
      putterLevelId,
      putterTypeId,
      putterHeadModelId: Number(putterHeadModelId),
      putterHeadFinishId: Number(putterHeadFinishId),
      putterSightlineId: Number(putterSightlineId),
      putterFaceId: Number(putterFaceId),
      putterNeckId: Number(putterNeckId),
      putterFinalHW,
      loft,
      lie,
      swingWt,
      soleWtId: String(soleWtId),
      stamping,
      painting,
      putterShaftId: Number(putterShaftId),
      measured,
      putterShaftLength,
      putterGripId: Number(putterGripId),
      putterGripTape,
      notes,
      itemNo,
    };

    // pass state and call handleSave
    await handleSave(putterClub);
  };

  const handleFinalHWChange = (newFinalHW: string) => {
    const masked = maskFloatInput(newFinalHW);
    setPutterFinalHW(masked); // update state with masked value
  };

  const handleFinalHWBlur = () => {
    // If input ends with a dot add a trailing '0'
    if (putterFinalHW.endsWith('.')) {
      setPutterFinalHW(putterFinalHW + '0');
    }
  };

  useEffect(() => {
    //const getData = async () => {
    //  let thisHeadModelWeights = await getHeadModelWeightsPutters();
    //  setHeadModelWeights(thisHeadModelWeights);
    //};
    //getData();

    if (putterDynamicDropdownData?.putterHeadModel) {
      if (putterHand !== '') {
        const putterHandId = handDropdownData?.map[putterHand].handId;
        setPutterHeadModelOptions(putterDynamicDropdownData.putterHeadModel.getOptions({ headModelHandIds: putterHandId, putterLevelIds: putterLevelId, putterTypeIds: putterTypeId }));
      }
    }
    if (putterDynamicDropdownData?.putterHeadFinish) {
      setPutterHeadFinishOptions(putterDynamicDropdownData.putterHeadFinish.getOptions({ putterLevelIds: putterLevelId, putterHeadModelIds: putterHeadModelId }));
    }
    if (putterDynamicDropdownData?.putterSightline) {
      setPutterSightlineOptions(putterDynamicDropdownData?.putterSightline.getOptions({ putterLevelIds: putterLevelId, putterHeadModelIds: putterHeadModelId }));
    }
    if (putterDynamicDropdownData?.putterFace) {
      setPutterFaceOptions(putterDynamicDropdownData?.putterFace.getOptions({ putterLevelIds: putterLevelId, putterHeadModelIds: putterHeadModelId }));
    }
    if (putterDynamicDropdownData?.putterNeck) {
      setPutterNeckOptions(putterDynamicDropdownData?.putterNeck.getOptions({ putterLevelIds: putterLevelId, putterHeadModelIds: putterHeadModelId }));
    }
    if (putterDynamicDropdownData?.putterShaft) {
      setPutterShaftOptions(putterDynamicDropdownData?.putterShaft.getOptions({ putterLevelIds: putterLevelId }));
    }
    if (putterDynamicDropdownData?.putterGrip) {
      setPutterGripOptions(putterDynamicDropdownData?.putterGrip.getOptions({ putterLevelIds: putterLevelId }));
    }
  }, [putterDynamicDropdownData, putterLevelId, putterHand, putterTypeId, putterHeadModelId, handDropdownData?.map]);

  if (!handDropdownData || !loftDropdownData || !lieDropdownData || !putterStaticDropdownData || !putterDynamicDropdownData || !shaftLengthDropdownData || !swingWtDropdownData) return null;

  return (
    <>
      <div className={`${clubStyles.tileRow} ${clubStyles.title}`}></div>
      <div className={clubStyles.tileRow}>
        <EditableDropdown label="Hand" initialId={putterHand} onChange={(hand) => setPutterHand(hand)} options={handDropdownData.options} />
        <EditableDropdown label="Level" initialId={putterLevelId} onChange={(levelId) => setPutterLevelId(Number(levelId))} options={putterStaticDropdownData.putterLevel.options} variant="large" />
        <EditableDropdown label="Type" initialId={putterTypeId} onChange={(typeId) => setPutterTypeId(Number(typeId))} options={putterStaticDropdownData.putterType.options} variant="large" />
        <EditableDropdown label="Model" initialId={putterHeadModelId} onChange={(headModelId) => setPutterHeadModelId(headModelId)} options={putterHeadModelOptions} variant="xxl" />
        <Toggle isActive={isActive} label={isActive ? 'In the Bag' : 'Out of Bag'} onChange={() => setIsActive((prevState) => !prevState)} />
      </div>
      <div className={clubStyles.tileRow}>
        <EditableDropdown label="Finish" initialId={putterHeadFinishId} onChange={(headFinishId) => setPutterHeadFinishId(headFinishId)} options={putterHeadFinishOptions} variant="large" />
        <EditableDropdown label="Sightline" initialId={putterSightlineId} onChange={(sightlineId) => setPutterSightlineId(sightlineId)} options={putterSightlineOptions} variant="large" />
        <EditableDropdown label="Face" initialId={putterFaceId} onChange={(faceId) => setPutterFaceId(faceId)} options={putterFaceOptions} variant="large" />
        <EditableDropdown label="Neck" initialId={putterNeckId} onChange={(neckId) => setPutterNeckId(neckId)} options={putterNeckOptions} variant="large" />
      </div>
      <div className={`${clubStyles.tileRow} ${clubStyles.rowSection}`}>
        <EditableDropdown label="Loft" initialId={loft} onChange={(loft) => setLoft(loft)} options={loftDropdownData.options} />
        <EditableDropdown label="Lie" initialId={lie} onChange={(lie) => setLie(lie)} options={lieDropdownData.options} />
        <EditableDropdown label="Swing Wt." initialId={swingWt} onChange={(swingWt) => setSwingWt(swingWt)} options={swingWtDropdownData.options} />
        <EditableDropdown label="Sole Wts." initialId={soleWtId} onChange={(soleWt) => setSoleWtId(soleWt)} options={putterStaticDropdownData.putterWeight.options} />
        <EditableTile
          label="Overall Wt. (g)"
          value={putterFinalHW.toString() ? putterFinalHW.toString() : ''}
          onChange={(newFinalHW) => handleFinalHWChange(newFinalHW)}
          onBlur={handleFinalHWBlur}
          variant="large"
        />
      </div>
      <div className={clubStyles.tileRow}>
        <div className={clubStyles.textAreaGroup}>
          <TextAreaInput value={stamping} label="Stamping" onChange={(value) => setStamping(value)} />
          <TextAreaInput value={painting} label="Painting" onChange={(value) => setPainting(value)} />
        </div>
      </div>
      <div className={clubStyles.tileRow}>
        <div className={clubStyles.categoryName}>Shaft</div>
      </div>
      <div className={clubStyles.tileRow}>
        <EditableDropdown label="Shaft" initialId={putterShaftId} onChange={(shaftId) => setPutterShaftId(shaftId)} options={putterShaftOptions} type="rectangle" alignment="left" variant="xxxxl" />
        {/* setting !measured to render the second options since its reversed in the designs */}
        <div className={clubStyles.radioGroup}>
          <RadioGroup selectedOption={!measured} firstLabel="Cut" secondLabel="Finished" onChange={() => setMeasured((prevState) => !prevState)} />
        </div>
      </div>
      <div className={`${clubStyles.tileRow} ${clubStyles.rowSection}`}>
        <EditableDropdown label="Length" initialId={putterShaftLength} onChange={(shaftLength) => setPutterShaftLength(shaftLength)} options={shaftLengthDropdownData.options} />
      </div>
      <div className={clubStyles.tileRow}>
        <div className={clubStyles.categoryName}>Grip</div>
      </div>
      <div className={`${clubStyles.tileRow} ${clubStyles.rowSection}`}>
        <EditableDropdown label="Grip" initialId={putterGripId} onChange={(grip) => setPutterGripId(grip)} options={putterGripOptions} variant="large" />
        <EditableTile label="Grip Tape" value={putterGripTape} onChange={(gripTape) => setPutterGripTape(gripTape)} variant="large" />
      </div>
      <div className={clubStyles.tileRow}>
        <div className={clubStyles.categoryName}>Notes</div>
      </div>
      <div className={clubStyles.tileRow}>
        <textarea className={clubStyles.editNotes} value={notes} name="notes" onChange={(event) => setNotes(event.target.value)} rows={5} />
      </div>
      <div className={clubStyles.buttonRow}>
        <button className={clubStyles.saveButton} onClick={savePutter}>
          Save
        </button>
        <button className={clubStyles.cancelButton} onClick={cancelEdition}>
          Cancel
        </button>
      </div>
    </>
  );
}

export default EditPutter;
